import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useEquipRing, useEquipment, useItems } from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useModalPayload, useUIStore } from '@/store/uiStore';
import { hapticNotify } from '@/lib/telegram';

interface Payload {
  waifuId: number;
}

export function RingModal() {
  const open = useUIStore((s) => s.isModalOpen('ring'));
  const closeModal = useUIStore((s) => s.closeModal);
  const payload = useModalPayload<Payload>('ring');
  const userId = useAuthStore((s) => s.userId) ?? 0;
  const pushNotification = useUIStore((s) => s.pushNotification);

  const waifuId = payload?.waifuId ?? 0;
  const equipRing = useEquipRing(waifuId, userId);
  const { data: equipmentData } = useEquipment(waifuId, userId);
  const { data: itemsData } = useItems();

  if (!open || !waifuId) return null;
  const alreadyEquipped = !!(
    equipmentData?.equipment.ring_equipped ?? equipmentData?.equipment.ring
  );

  const ring = itemsData?.items.find((i) => i.item_id === 'wedding_ring');
  const ringHp = ring?.hp_bonus ?? 0;
  const ringStr = ring?.str_bonus ?? 0;
  const ringDex = ring?.dex_bonus ?? 0;
  const ringInt = ring?.int_bonus ?? 0;

  const bonusRows: Array<{ label: string; value: number }> = [
    { label: 'HP', value: ringHp },
    { label: 'STR', value: ringStr },
    { label: 'DEX', value: ringDex },
    { label: 'INT', value: ringInt },
  ].filter((b) => b.value > 0);

  return (
    <Modal
      open={open}
      onClose={() => closeModal('ring')}
      title={alreadyEquipped ? 'Вы обручены' : 'Обручальное кольцо'}
    >
      <div className="p-4 space-y-4 text-center">
        <img
          src="/data/items/wedding_ring.webp"
          alt="Обручальное кольцо"
          className="w-20 h-20 object-contain mx-auto"
          draggable={false}
        />

        {alreadyEquipped ? (
          <>
            <p className="text-white/85 leading-relaxed">
              {ring?.description ??
                'Связь скреплена клятвами. Кольцо нельзя снять.'}
            </p>
            {bonusRows.length > 0 && (
              <div className="rounded-xl bg-bg-elevated border border-white/5 p-3 space-y-1 text-left">
                <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
                  Бонусы кольца
                </div>
                <div className="space-y-0.5">
                  {bonusRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between text-[13px] tabular-nums"
                    >
                      <span className="text-white/55">{row.label}</span>
                      <span className="text-success font-medium">
                        +{row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-white/80 leading-relaxed">
              Свяжи свою судьбу с этой waifu клятвой. Это даст постоянный
              бонус, но кольцо нельзя снять.
            </p>
            {bonusRows.length > 0 && (
              <div className="rounded-xl bg-bg-elevated border border-white/5 p-3 space-y-1 text-left">
                <div className="text-[11px] uppercase tracking-wider text-white/45 font-semibold">
                  Бонусы кольца
                </div>
                <div className="space-y-0.5">
                  {bonusRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-center justify-between text-[13px] tabular-nums"
                    >
                      <span className="text-white/55">{row.label}</span>
                      <span className="text-success font-medium">
                        +{row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button
              fullWidth
              onClick={async () => {
                try {
                  await equipRing.mutateAsync();
                  hapticNotify('success');
                  pushNotification({
                    type: 'success',
                    message: 'Вы обручены!',
                  });
                  closeModal('ring');
                } catch (err) {
                  hapticNotify('error');
                  const message =
                    err instanceof Error ? err.message : 'Не удалось обручиться';
                  pushNotification({ type: 'error', message });
                }
              }}
              disabled={equipRing.isPending}
            >
              {equipRing.isPending ? '…' : 'Обменяться клятвами'}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
}
